import { FONT_FAMILIES } from "../mail/richtext/editorCommands";
import { aktiveAuswahl, aktivesDokument } from "../windowManager/browserFenster/fensterRegister";

/**
 * Die Formatier-Palette der Notizblöcke (Design-Stand 08.09.2026: „Rechtsklick
 * öffnet die schwebende Formatierungsleiste nach OneNote-Vorbild").
 *
 * ══ WARUM KLASSEN UND NICHT `style` ════════════════════════════════════════
 * Ein Block läuft durch `reinigeBlockHtml` (`blockHtml.ts`) — und zwar
 * ZWEIMAL: beim Speichern und beim Einsetzen in die Fläche. Dort ist `style`
 * ausdrücklich verboten und `<font>` gar nicht erst erlaubt. Alles, was
 * `execCommand` für Schriftart, Größe, Farbe und Marker erzeugt, fällt also
 * weg — `fontName` liefert `<font face>`, `foreColor` und `hiliteColor`
 * liefern `style`, und `applyFontSize` aus dem Mail-Editor schreibt bewusst
 * `style="font-size"`. Man sähe die Auszeichnung beim Tippen und beim
 * nächsten Laden wäre sie fort, ohne jede Fehlermeldung.
 *
 * Deshalb vergibt die Palette KLASSEN (`class` ist erlaubt) und die Optik
 * steht in `notizbloecke.css`. Den Reiniger zu öffnen wäre die Alternative
 * gewesen — er trägt aber auch das Kundentagebuch, und er läuft
 * auch beim LESEN: ein in V4 farbig geschriebener Block sähe im Tagebuch sonst
 * anders aus.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ WIE DIE AUSWAHL EINGEHÜLLT WIRD ════════════════════════════════════════
 * `execCommand` kann keine Klasse setzen. Statt die Auswahl selbst zu
 * umschließen (`Range.surroundContents` scheitert, sobald sie über eine
 * Elementgrenze läuft), benutzt die Palette denselben Trick wie
 * `applyFontSize`: `fontSize` mit der alten HTML-Größe 7 als MARKER anwenden —
 * der Browser zerlegt die Auswahl korrekt und hüllt jedes Stück in
 * `<font size="7">` — und diese Marker anschließend in Klassen-Hüllen
 * umschreiben ({@link markerZuKlasse}). Der DOM-Teil ist dadurch eine reine
 * Funktion und im jsdom prüfbar; `execCommand` bleibt in {@link setzeKlasse}.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Die HTML-Größe, die als Marker dient — siehe Modulkopf. */
export const MARKER_GROESSE = "7";

/* ── Die vier Gruppen ─────────────────────────────────────────────────────
 * Eine Gruppe ist der Klassenpräfix. Je Gruppe trägt ein Textstück HÖCHSTENS
 * EINE Klasse: beim Setzen räumt `markerZuKlasse` die alte derselben Gruppe
 * weg. Ohne das stünde die alte Größe verschachtelt in der neuen und gewänne
 * per Spezifität — man klickte 24 pt und nichts geschähe. */
export const GRUPPE_SCHRIFT = "nb-schrift-";
export const GRUPPE_GROESSE = "nb-fs-";
export const GRUPPE_FARBE = "nb-farbe-";
export const GRUPPE_MARKER = "nb-mark-";

/** Die Gruppe einer Klasse — alles bis zum letzten Bindestrich. */
export function gruppeVon(klasse: string): string {
    return klasse.slice(0, klasse.lastIndexOf("-") + 1);
}

/** Kurzname je Schriftart; er bildet die Klasse `nb-schrift-<slug>`. */
const SCHRIFT_SLUGS: Record<string, { slug: string; stapel: string }> = {
    "Times New Roman": { slug: "times", stapel: '"Times New Roman", Times, serif' },
    "Segoe UI": { slug: "segoe", stapel: '"Segoe UI", system-ui, sans-serif' },
    Arial: { slug: "arial", stapel: "Arial, Helvetica, sans-serif" },
    "Courier New": { slug: "courier", stapel: '"Courier New", Courier, monospace' },
};

/**
 * Die angebotenen Schriftarten — DIESELBEN wie im Mail-Editor
 * (`FONT_FAMILIES`). Ein zweites Angebot nebenan wäre eine zweite Wahrheit;
 * der Test hält beide Listen aneinander.
 */
export const BLOCK_SCHRIFTEN: Array<{ klasse: string; label: string; stapel: string }> =
    FONT_FAMILIES.filter((f) => SCHRIFT_SLUGS[f]).map((f) => ({
        klasse: `${GRUPPE_SCHRIFT}${SCHRIFT_SLUGS[f]!.slug}`,
        label: f,
        stapel: SCHRIFT_SLUGS[f]!.stapel,
    }));

/**
 * Die angebotenen Größen in pt. Bewusst NICHT die 29 Einerschritte des
 * Mail-Editors: jede Größe braucht hier eine CSS-Regel, und eine Notiz ist
 * kein Satzprogramm. Die Stufen sind die von OneNote.
 */
export const BLOCK_GROESSEN: Array<{ klasse: string; pt: number }> = [
    9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 36,
].map((pt) => ({ klasse: `${GRUPPE_GROESSE}${pt}`, pt }));

/**
 * Schriftfarben. Die Palette ist bewusst klein und aus den Tokens der
 * Design-Guideline: Aubergine als Aktivfarbe, dazu die Farben, die in V4
 * ohnehin für Mail, Anruf und Risiko stehen. Ein freier Farbwähler erzeugte
 * Klassen, die es im CSS nicht gibt.
 */
export const BLOCK_FARBEN: Array<{ klasse: string; label: string; hex: string }> = [
    { klasse: `${GRUPPE_FARBE}aubergine`, label: "Aubergine", hex: "#7d2169" },
    { klasse: `${GRUPPE_FARBE}rot`, label: "Rot", hex: "#b3261e" },
    { klasse: `${GRUPPE_FARBE}blau`, label: "Blau", hex: "#1a5fa8" },
    { klasse: `${GRUPPE_FARBE}gruen`, label: "Grün", hex: "#2f7d5b" },
    { klasse: `${GRUPPE_FARBE}orange`, label: "Orange", hex: "#a35400" },
];

/** Textmarker. Gerendert als `<mark>` — das Zeichen ist im Reiniger erlaubt. */
export const BLOCK_MARKER: Array<{ klasse: string; label: string; hex: string }> = [
    { klasse: `${GRUPPE_MARKER}gelb`, label: "Gelb", hex: "#fff3a3" },
    { klasse: `${GRUPPE_MARKER}gruen`, label: "Grün", hex: "#c7efd4" },
    { klasse: `${GRUPPE_MARKER}blau`, label: "Blau", hex: "#cfe6ff" },
    { klasse: `${GRUPPE_MARKER}rosa`, label: "Rosa", hex: "#ffd6ec" },
];

/** Hülle auflösen: die Kinder rücken an ihre Stelle. */
function entferneHuelle(el: Element): void {
    const eltern = el.parentNode;
    if (!eltern) return;
    while (el.firstChild) eltern.insertBefore(el.firstChild, el);
    eltern.removeChild(el);
}

/**
 * Die Klassen EINER Gruppe aus einem Teilbaum räumen. Bleibt eine Hülle
 * dadurch klassenlos, verschwindet sie mit — sonst sammelte ein Block bei
 * jedem Formatieren eine weitere leere `<span>`-Schicht an.
 */
function raeumeGruppe(knoten: Node, gruppe: string, mitSelbst: boolean): void {
    if (knoten.nodeType !== 1) return;
    const wurzel = knoten as Element;
    const elemente = [
        ...(mitSelbst ? [wurzel] : []),
        ...Array.from(wurzel.querySelectorAll("*")),
    ];
    for (const el of elemente) {
        const treffer = Array.from(el.classList).filter((c) => c.startsWith(gruppe));
        if (treffer.length) {
            el.classList.remove(...treffer);
            if (el.classList.length === 0) el.removeAttribute("class");
        }
        const tag = el.tagName.toLowerCase();
        // `<mark>` ohne Klasse zählt zur Markergruppe: es IST der Marker.
        // Sonst bliebe eingefügter oder älterer Text nach „kein Marker" gelb.
        const gehoertDazu = treffer.length > 0 || (gruppe === GRUPPE_MARKER && tag === "mark");
        if (gehoertDazu && !el.hasAttribute("class") && (tag === "span" || tag === "mark")) {
            entferneHuelle(el);
        }
    }
}

/**
 * Die von `execCommand` gesetzten Marker in Klassen-Hüllen umschreiben.
 *
 * REINE DOM-Arbeit, damit der jsdom-Runner sie prüfen kann — `execCommand`
 * gibt es dort nicht (siehe {@link setzeKlasse}).
 *
 * `klasse === null` heißt „Standard" bzw. „kein Marker": die Marker werden nur
 * aufgelöst und die Klassen der Gruppe aus der Auswahl geräumt.
 */
export function markerZuKlasse(
    root: HTMLElement | null,
    klasse: string | null,
    gruppe: string,
    tag: "span" | "mark" = "span",
): void {
    if (!root) return;
    const doc = root.ownerDocument;
    const marker = Array.from(root.querySelectorAll(`font[size="${MARKER_GROESSE}"]`));
    for (const m of marker) {
        const eltern = m.parentNode;
        if (!eltern) continue;
        if (klasse) {
            const huelle = doc.createElement(tag);
            huelle.className = klasse;
            while (m.firstChild) huelle.appendChild(m.firstChild);
            eltern.replaceChild(huelle, m);
            // Nur die NACHFAHREN räumen: die Hülle selbst trägt die neue Klasse.
            raeumeGruppe(huelle, gruppe, false);
            continue;
        }
        // Auflösen. Die Kinder werden vor dem Umhängen festgehalten — danach
        // stehen sie im Elternknoten und wären nicht mehr zu finden.
        const kinder = Array.from(m.childNodes);
        for (const k of kinder) eltern.insertBefore(k, m);
        eltern.removeChild(m);
        for (const k of kinder) raeumeGruppe(k, gruppe, true);
    }
}

/**
 * Die aktuelle Auswahl in eine Klassen-Hülle legen (oder die Gruppe entfernen).
 *
 * Der `execCommand`-Teil der Palette: er markiert die Auswahl mit
 * `<font size="7">`, {@link markerZuKlasse} macht Klassen daraus.
 * `styleWithCSS` wird ausdrücklich abgeschaltet — sonst erzeugt Chrome ein
 * `<span style>`, und `style` fällt im Reiniger weg.
 */
export function setzeKlasse(
    root: HTMLElement | null,
    klasse: string | null,
    gruppe: string,
    tag: "span" | "mark" = "span",
): void {
    if (!root) return;
    root.focus();
    // eslint-disable-next-line no-restricted-syntax
    aktivesDokument().execCommand("styleWithCSS", false, "false");
    // eslint-disable-next-line no-restricted-syntax
    aktivesDokument().execCommand("fontSize", false, MARKER_GROESSE);
    markerZuKlasse(root, klasse, gruppe, tag);
}

/* ── Formatierung aufheben (T-282) ─────────────────────────────────────────
 * „Formatierung entfernen" nimmt der Auswahl ALLES, was die Palette und das
 * Einfügen mitbringen: die vier Klassengruppen, `<mark>` und die echten Tags
 * fett/kursiv/unterstrichen/durchgestrichen. Listen, Absätze, Links, Bilder,
 * `#`-Bezüge, `@`-Erwähnungen und markierte Stellen (Kommentar/Erledigt)
 * bleiben — das ist Inhalt, keine Auszeichnung.
 *
 * Derselbe Marker-Trick wie oben, mit einem Unterschied: Liegt die Auswahl
 * INNERHALB einer Hülle (ein Wort mitten in einem 18-pt-Satz), legt der
 * Browser den Marker in die Hülle — die äußere Klasse wirkte weiter. Deshalb
 * wird jede Format-Hülle über dem Marker um ihn herum AUFGESPALTEN: links und
 * rechts bleibt sie, das markierte Stück kommt heraus. */

const FORMAT_TAGS = new Set(["b", "strong", "i", "em", "u", "s", "strike", "del", "mark", "font", "sub", "sup"]);
const FORMAT_GRUPPEN = [GRUPPE_SCHRIFT, GRUPPE_GROESSE, GRUPPE_FARBE, GRUPPE_MARKER];

/** Ist das Element reine Auszeichnung, die „Formatierung entfernen" auflöst? */
function istFormatHuelle(el: Element): boolean {
    const tag = el.tagName.toLowerCase();
    if (tag === "font") return el.getAttribute("size") !== MARKER_GROESSE;
    if (FORMAT_TAGS.has(tag)) return true;
    if (tag !== "span") return false;
    // Bezüge, Erwähnungen und markierte Stellen sind Inhalt.
    if (el.hasAttribute("data-ref-art") || el.hasAttribute("data-anker") || el.hasAttribute("data-von")) return false;
    const klassen = Array.from(el.classList);
    return klassen.every((k) => FORMAT_GRUPPEN.some((g) => k.startsWith(g)));
}

/** Ist nach dem Aufspalten etwas Sichtbares übrig? Leere Hälften fliegen. */
function hatInhalt(el: Element): boolean {
    return (el.textContent ?? "") !== "" || !!el.querySelector("img,br");
}

/**
 * `huelle` um `kern` (einen Nachfahren) herum aufspalten: was links und rechts
 * von `kern` steht, bekommt je eine Kopie der Hülle; danach enthält `huelle`
 * nur noch den Pfad zu `kern`.
 */
function spalteUm(huelle: Element, kern: Node): void {
    const doc = huelle.ownerDocument;
    const eltern = huelle.parentNode;
    if (!eltern) return;
    const links = doc.createRange();
    links.setStart(huelle, 0);
    links.setEndBefore(kern);
    const linksTeil = links.extractContents();
    const rechts = doc.createRange();
    rechts.setStartAfter(kern);
    rechts.setEnd(huelle, huelle.childNodes.length);
    const rechtsTeil = rechts.extractContents();
    const linksKopie = huelle.cloneNode(false) as Element;
    linksKopie.appendChild(linksTeil);
    if (hatInhalt(linksKopie)) eltern.insertBefore(linksKopie, huelle);
    const rechtsKopie = huelle.cloneNode(false) as Element;
    rechtsKopie.appendChild(rechtsTeil);
    if (hatInhalt(rechtsKopie)) eltern.insertBefore(rechtsKopie, huelle.nextSibling);
}

/**
 * Die von `execCommand("fontSize")` gesetzten Marker auflösen und dabei jede
 * Formatierung des markierten Stücks entfernen — reine DOM-Arbeit, im jsdom
 * prüfbar (siehe {@link entferneFormatierung}).
 */
export function formatierungAufloesen(root: HTMLElement | null): void {
    if (!root) return;
    const marker = Array.from(root.querySelectorAll(`font[size="${MARKER_GROESSE}"]`));
    for (const m of marker) {
        if (!root.contains(m)) continue;
        // 1. Hüllen ÜBER dem Marker aufspalten und auflösen.
        let a = m.parentElement;
        while (a && a !== root && root.contains(a)) {
            const weiter = a.parentElement;
            if (istFormatHuelle(a)) {
                spalteUm(a, m);
                entferneHuelle(a);
            }
            a = weiter;
        }
        // 2. Hüllen IM Marker auflösen.
        for (const el of Array.from(m.querySelectorAll("*"))) {
            if (istFormatHuelle(el)) entferneHuelle(el);
        }
        // 3. Den Marker selbst.
        entferneHuelle(m);
    }
}

/**
 * „Formatierung entfernen" an der aktuellen Auswahl. Ohne Markierung
 * geschieht nichts — es gibt kein Stück, dem etwas zu nehmen wäre.
 */
export function entferneFormatierung(root: HTMLElement | null): void {
    if (!root) return;
    const sel = aktiveAuswahl();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    root.focus();
    // eslint-disable-next-line no-restricted-syntax
    aktivesDokument().execCommand("styleWithCSS", false, "false");
    // eslint-disable-next-line no-restricted-syntax
    aktivesDokument().execCommand("fontSize", false, MARKER_GROESSE);
    formatierungAufloesen(root);
}
